import NodeRSA from "node-rsa";
import {RSA_PUBLIC_KEY} from './config';
import moment from 'moment-timezone';
import * as XLSX from 'xlsx';

export const PAGE_SIZE_OPTIONS = [20, 50, 100]

export const DEFAULT_ITEM_PER_PAGE = 20

export const FULL_LIST_ITEM_PER_PAGE = 100

const ITEM_PER_PAGE_STORAGE_KEY = 'itemPerPage'

const readStoredItemPerPage = () => {
    const stored = Number(localStorage.getItem(ITEM_PER_PAGE_STORAGE_KEY))
    return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_ITEM_PER_PAGE
}

let currentItemPerPage = readStoredItemPerPage()

const itemPerPageListeners = new Set()

export const getItemPerPage = () => currentItemPerPage

export const setItemPerPage = (value) => {
    if (!PAGE_SIZE_OPTIONS.includes(value) || value === currentItemPerPage) {
        return
    }
    currentItemPerPage = value
    localStorage.setItem(ITEM_PER_PAGE_STORAGE_KEY, String(value))
    itemPerPageListeners.forEach((listener) => listener(value))
}

export const subscribeItemPerPage = (listener) => {
    itemPerPageListeners.add(listener)
    return () => {
        itemPerPageListeners.delete(listener)
    }
}

export const formatNumber = (num) => {
    return new Intl.NumberFormat('vn-VI').format(num || 0)
}

export const  parseQuery = (queryString) => {
    var query = {};
    if (queryString) {
        var pairs = (queryString[0] === '?' ? queryString.substr(1) : queryString).split('&');
        for (var i = 0; i < pairs.length; i++) {
            var pair = pairs[i].split('=');
            query[decodeURIComponent(pair[0])] = JSON.parse(decodeURIComponent(pair[1] || ''));
        }
    }
    return query;
}

export const calculatePage = (total, itemPerPage = getItemPerPage()) => {
    let pages = Math.floor(total / itemPerPage)
    if (total % itemPerPage === 0) {
        return pages
    }
    return pages + 1
}

const XLSX_MAX_COLUMN_WIDTH = 50;

export const downloadXlsx = (rows, callback) => {
    const worksheet = XLSX.utils.json_to_sheet(rows);

    const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
    worksheet['!cols'] = headers.map((header) => {
        const contentWidth = Math.max(
            header.length,
            ...rows.map((row) => String(row[header] ?? '').length)
        );
        return { wch: Math.min(XLSX_MAX_COLUMN_WIDTH, contentWidth + 2) };
    });

    if (worksheet['!ref']) {
        worksheet['!autofilter'] = { ref: worksheet['!ref'] };
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dữ liệu');
    XLSX.writeFile(workbook, new Date().getTime() + '.xlsx');

    if (callback) {
        callback();
    }
}

export const parseWeekday = (day) => {
    switch (day) {
        case 'Mon':
            return 'Thứ hai'
        case 'Tue':
            return 'Thứ ba'
        case 'Wed':
            return 'Thứ tư'
        case 'Thu':
            return 'Thứ năm'
        case 'Fri':
            return 'Thứ sáu'
        case 'Sat':
            return 'Thứ bảy'
        case 'Sun':
            return 'Chủ nhật'
        default:
            return '';
    }
}

export const getClassicWeekdayKey = (date) => {
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayIndex = moment(date).day();
    return weekdays[dayIndex];
}

export const encryptMessage = (message) => {

    const key = new NodeRSA(RSA_PUBLIC_KEY);

    const encrypted = key.encrypt(message, 'base64');

    return encrypted;
}

/**
 * Convert UTC timestamp to Vietnam local time (GMT+7)
 * @param {string|Date} utcTime - UTC timestamp from database
 * @returns {string} - Formatted local time string (HH:mm)
 */
export const convertToVietnamTime = (utcTime) => {
    if (!utcTime) return '';

    try {
        // Convert to Vietnam timezone (Asia/Ho_Chi_Minh)
        const vietnamTime = moment(utcTime).tz('Asia/Ho_Chi_Minh');
        return vietnamTime.format('HH:mm');
    } catch (error) {
        console.error('Error converting time to Vietnam timezone:', error);
        return utcTime; // Return original time if conversion fails
    }
}

// Cấu trúc giải thưởng xổ số truyền thống (Miền Nam)
export const CLASSIC_REWARDS = [
    { id: 'GIAI_DB', name: 'Giải ĐB', value: 2000000000, lastDigits: 6, order: 0 },
    { id: 'GIAI_NHAT', name: 'Giải Nhất', value: 30000000, lastDigits: 5, order: 1 },
    { id: 'GIAI_NHI', name: 'Giải Nhì', value: 15000000, lastDigits: 5, order: 2 },
    { id: 'GIAI_BA', name: 'Giải Ba', value: 10000000, lastDigits: 5, order: 3 },
    { id: 'GIAI_TU', name: 'Giải Tư', value: 3000000, lastDigits: 5, order: 4 },
    { id: 'GIAI_NAM', name: 'Giải Năm', value: 1000000, lastDigits: 4, order: 5 },
    { id: 'GIAI_SAU', name: 'Giải Sáu', value: 400000, lastDigits: 4, order: 6 },
    { id: 'GIAI_BAY', name: 'Giải Bảy', value: 200000, lastDigits: 3, order: 7 },
    { id: 'GIAI_TAM', name: 'Giải Tám', value: 100000, lastDigits: 2, order: 8 },
    { id: 'GIAI_PHU_DB', name: 'Giải Phụ ĐB', value: 50000000, lastDigits: 5, order: 9 },
    { id: 'GIAI_KK', name: 'Giải KK', value: 6000000, lastDigits: 5, order: 10 },
];

/**
 * Phân tích kết quả xổ số thành mảng 2 chiều
 * @param {string[]} result - Mảng kết quả từ API
 * @returns {string[][]} - Mảng 2 chiều đã phân tích
 */
export const classicResultAnalytic = (result) => {
    if (!result || result.length === 0) return [];

    return result.map(r => r.split(' '));
};

/**
 * Đếm số lần khớp của số vé với kết quả (theo số chữ số cuối)
 * @param {string} ticketNumbers - Số vé (6 chữ số)
 * @param {string[]} resultList - Danh sách kết quả
 * @param {number} lastDigits - Số chữ số cuối để so khớp
 * @returns {string[]} - Danh sách các số khớp
 */
const countClassicNumberMatchInResultList = (ticketNumbers, resultList, lastDigits) => {
    if (!resultList || resultList.length === 0) return [];

    const matchedList = [];
    const ticketLast = ticketNumbers.slice(-lastDigits);

    for (const resultNumber of resultList) {
        const resultLast = resultNumber.slice(-lastDigits);
        if (ticketLast === resultLast) {
            matchedList.push(resultNumber);
        }
    }

    return matchedList;
};

/**
 * Kiểm tra giải khuyến khích (5 số cuối trùng vị trí nhưng khác 1 số)
 * @param {string} ticketNumbers - Số vé
 * @param {string} dbNumber - Số giải đặc biệt
 * @returns {string[]} - Mảng các chữ số trùng
 */
const countClassicNumbeMatchInResultListForKhuyenKhich = (ticketNumbers, dbNumber) => {
    if (!dbNumber) return [];

    const matchedList = [];
    const ticketDigits = ticketNumbers.split('');
    const dbDigits = dbNumber.split('');

    for (let i = 0; i < dbDigits.length; i++) {
        if (ticketDigits[i] === dbDigits[i]) {
            matchedList.push(ticketDigits[i]);
        } else {
            matchedList.push('');
        }
    }

    return matchedList;
};

/**
 * Tính toán giải thưởng xổ số truyền thống
 * @param {string} ticketNumbers - Số vé (6 chữ số)
 * @param {string[][]} analyticResult - Kết quả đã phân tích
 * @returns {object} - Thông tin giải thưởng
 */
export const calculateClassicReward = (ticketNumbers, analyticResult) => {
    if (!ticketNumbers || !analyticResult || analyticResult.length < 9) {
        return { rewards: [], matches: {}, totalReward: 0, rewardNames: [] };
    }

    const rewards = [];
    const matches = {
        g_db: [],
        g_pdb: [],
        g_kk: [],
        g_1: [],
        g_2: [],
        g_3: [],
        g_4: [],
        g_5: [],
        g_6: [],
        g_7: [],
        g_8: [],
    };

    const listReward = CLASSIC_REWARDS;

    // Giải ĐB (6 số cuối)
    matches.g_db = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[0], listReward[0].lastDigits);

    // Giải Phụ ĐB (5 số cuối của giải ĐB)
    matches.g_pdb = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[0], listReward[9].lastDigits);

    // Giải Khuyến Khích
    matches.g_kk = countClassicNumbeMatchInResultListForKhuyenKhich(ticketNumbers, analyticResult[0][0]);

    // Giải Nhất (5 số cuối)
    matches.g_1 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[1], listReward[1].lastDigits);

    // Giải Nhì (5 số cuối)
    matches.g_2 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[2], listReward[2].lastDigits);

    // Giải Ba (5 số cuối)
    matches.g_3 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[3], listReward[3].lastDigits);

    // Giải Tư (5 số cuối)
    matches.g_4 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[4], listReward[4].lastDigits);

    // Giải Năm (4 số cuối)
    matches.g_5 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[5], listReward[5].lastDigits);

    // Giải Sáu (4 số cuối)
    matches.g_6 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[6], listReward[6].lastDigits);

    // Giải Bảy (3 số cuối)
    matches.g_7 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[7], listReward[7].lastDigits);

    // Giải Tám (2 số cuối)
    matches.g_8 = countClassicNumberMatchInResultList(ticketNumbers, analyticResult[8], listReward[8].lastDigits);

    let totalReward = 0;
    const rewardNames = [];

    // Tính giải thưởng
    if (matches.g_db.length > 0) {
        rewards.push({ rewardId: 'GIAI_DB', count: 1 });
        totalReward += listReward[0].value;
        rewardNames.push('Giải ĐB');
    }

    if (matches.g_pdb.length > 0 && matches.g_db.length === 0) {
        rewards.push({ rewardId: 'GIAI_PHU_DB', count: 1 });
        totalReward += listReward[9].value;
        rewardNames.push('Giải Phụ ĐB');
    } else {
        // Kiểm tra giải khuyến khích (5 số đúng vị trí liên tiếp)
        const kkCount = matches.g_kk.filter(it => it !== '');
        if (kkCount.length === 5) {
            rewards.push({ rewardId: 'GIAI_KK', count: 1 });
            totalReward += listReward[10].value;
            rewardNames.push('Giải KK');
        }
    }

    if (matches.g_1.length > 0) {
        rewards.push({ rewardId: 'GIAI_NHAT', count: matches.g_1.length });
        totalReward += listReward[1].value * matches.g_1.length;
        rewardNames.push('Giải Nhất');
    }

    if (matches.g_2.length > 0) {
        rewards.push({ rewardId: 'GIAI_NHI', count: matches.g_2.length });
        totalReward += listReward[2].value * matches.g_2.length;
        rewardNames.push('Giải Nhì');
    }

    if (matches.g_3.length > 0) {
        rewards.push({ rewardId: 'GIAI_BA', count: matches.g_3.length });
        totalReward += listReward[3].value * matches.g_3.length;
        rewardNames.push('Giải Ba');
    }

    if (matches.g_4.length > 0) {
        rewards.push({ rewardId: 'GIAI_TU', count: matches.g_4.length });
        totalReward += listReward[4].value * matches.g_4.length;
        rewardNames.push('Giải Tư');
    }

    if (matches.g_5.length > 0) {
        rewards.push({ rewardId: 'GIAI_NAM', count: matches.g_5.length });
        totalReward += listReward[5].value * matches.g_5.length;
        rewardNames.push('Giải Năm');
    }

    if (matches.g_6.length > 0) {
        rewards.push({ rewardId: 'GIAI_SAU', count: matches.g_6.length });
        totalReward += listReward[6].value * matches.g_6.length;
        rewardNames.push('Giải Sáu');
    }

    if (matches.g_7.length > 0) {
        rewards.push({ rewardId: 'GIAI_BAY', count: matches.g_7.length });
        totalReward += listReward[7].value * matches.g_7.length;
        rewardNames.push('Giải Bảy');
    }

    if (matches.g_8.length > 0) {
        rewards.push({ rewardId: 'GIAI_TAM', count: matches.g_8.length });
        totalReward += listReward[8].value * matches.g_8.length;
        rewardNames.push('Giải Tám');
    }

    return { rewards, matches, totalReward, rewardNames };
}
